
import Vue from 'vue';
import _ from 'lodash';
import { numberAbbreviation } from '@/helpers/numberAbbreviation';
import { numFormat } from '@/helpers/numFormat';
import { productAlias } from '@/helpers/productAlias';

interface ProjectedItem {
  itemId: number
  product?: any
  quantities: {
    q1: number
    q2: number
    q3: number
    q4: number
    totalSum?: number
  }
}
export default Vue.extend({
  props: {
    input: { type: Object, default: () => {} },
    data: { type: Object, default: () => {} },
    getVariable: { type: Function, default: () => () => {} },
    callFunction: { type: Function, default: () => () => {} },
    onChange: { type: Function, default: () => {} },
    parent: { type: Object, default: () => {} },
    rowDisabled: { type: Boolean, default: false }
  },
  data(): { projectedItems: Array<ProjectedItem>, productId: number | undefined } {
    return { projectedItems: [], productId: undefined };
  },
  computed: {
    forecastData: function(): any {
      return this.getVariable(this.input.forecastDataVariable);
    },
    groupedInvoicedItems: function(): any[] {
      const currentInvoicedItems = _.cloneDeep(this.forecastData.invoicedItems);
      const invoicedItems = this.projectedItems.map((projectedItem: ProjectedItem) => {
        const currentInvoicedItem: ProjectedItem | undefined = currentInvoicedItems.find((invoicedItem: ProjectedItem) => invoicedItem.itemId == projectedItem.itemId);
        if(!currentInvoicedItem) {
          return {
            itemId: projectedItem.itemId,
            product: this.productData(projectedItem.itemId),
            quantities: { q1: 0, q2: 0, q3: 0, q4: 0, totalSum: 0 }
          };
        } else return { ...currentInvoicedItem, product: this.productData(currentInvoicedItem.itemId) };
      });
      const groupedItems = _.groupBy(invoicedItems, 'product.productLine');
      const groupedInvoicedItems = _.map(Object.keys(groupedItems), (productLine: string) => {
        const subProducts = groupedItems[productLine].sort(
          (invoicedA: ProjectedItem, invoicedB: ProjectedItem) => (invoicedB.quantities.totalSum || 0) - (invoicedA.quantities.totalSum || 0)
        ).map(
          (invoicedItem: ProjectedItem) => {
            let projectedItem = _.find(this.projectedItems, (projectedItem: ProjectedItem) => projectedItem.itemId == invoicedItem.itemId);
            if(!projectedItem) {
              projectedItem = {
                itemId: invoicedItem.itemId,
                quantities: {
                  q1: 0,
                  q2: 0,
                  q3: 0,
                  q4: 0,
                  totalSum: 0
                }
              };
            }
            return { invoicedItem, projectedItem };
          });
        const productLineData = {
          productLine,
          invoicedItem: { quantities: this.calculateQuantitySum(subProducts.map(product => product.invoicedItem)) },
          projectedItem: { quantities: this.calculateQuantitySum(subProducts.map(product => product.projectedItem)) },
          subProducts
        };
        return productLineData;
      });
      return groupedInvoicedItems.sort(
        (groupA: any, groupB: any) => groupB.invoicedItem.quantities.totalSum - groupA.invoicedItem.quantities.totalSum
      );
    },
    displayInSAR: function(): boolean {
      return this.getVariable(this.input.displaySarVariable) || false;
    },
    customerProductList: function(): any[] {
      return this.getVariable(this.input.productsVariable) || [];
    },
    filteredProducts: function(): any[] {
      const projectedItemIds = this.projectedItems.map((projectedItem: ProjectedItem) => projectedItem.itemId);
      const filteredProducts = this.customerProductList.filter((product: any) => !projectedItemIds.includes(product._id) && product.status !== 'Closed');
      return filteredProducts.map((product: any) => {
        return {
          text: `${product._id} ${productAlias(product)} (${product.status})`,
          value: product._id
        };
      });
    },
    disabled: function(): boolean {
      return this.rowDisabled || this.parent._getDisableStatus(this.input.disabled) || this.displayInSAR;
    },
  },
  watch: {
    forecastData() {
      this.loadProjectedItems();
    }
  },
  mounted() {
    this.loadProjectedItems();
    this.$watch(
      () => this.parent._getVariable(this.input.projectedItemsVariable),
      (projectedItems) => {
        if (projectedItems) {
          this.projectedItems = projectedItems;
        }
      },
      {
        immediate: true, deep: true
      }
    );
  },
  methods: {
    loadProjectedItems: function() {
      let projectedItems: ProjectedItem[] = this.forecastData.projectedItems;
      _.forEach(this.forecastData.invoicedItems, (invoicedItem: any) => {
        const itemId: number = invoicedItem.itemId;
        if(itemId) {
          const existingProjectedItem = _.find(projectedItems, (projectedItem: ProjectedItem) => projectedItem.itemId == itemId);
          if(!existingProjectedItem) {
            projectedItems.push({
              itemId,
              quantities: {
                q1: 0,
                q2: 0,
                q3: 0,
                q4: 0,
                totalSum: 0
              }
            });
          }
        }
      });
      const { prefix, key } = this.parent._getPrefixAndKey(this.input.projectedItemsVariable);
      this.$set(this.getVariable(prefix), key, projectedItems);
    },
    calculateQuantitySum: function(forecastItems: ProjectedItem[]): { q1: number, q2: number, q3: number, q4: number, totalSum: number } {
      const calculateTotalQuantity = (inputForecastItems: ProjectedItem[], type: string) => {
        return inputForecastItems.reduce(
          (quantity: number, forecastItem: ProjectedItem) => {
            if(type == 'totalSum') return quantity + this.getTotalSum(forecastItem);
            else return quantity + Number(_.get(forecastItem, `quantities.${type}`, 0))
          },
          0
        )
      }
      return {
        q1: calculateTotalQuantity(forecastItems, 'q1'),
        q2: calculateTotalQuantity(forecastItems, 'q2'),
        q3: calculateTotalQuantity(forecastItems, 'q3'),
        q4: calculateTotalQuantity(forecastItems, 'q4'),
        totalSum: calculateTotalQuantity(forecastItems, 'totalSum')
      };
    },
    productData: function(productId: number) {
      if(!productId) return {};
      const productData: any = this.customerProductList.find((product: any) => product._id == productId);
      return productData;
    },
    baseUnit: function(productData: any) {
      return _.get(productData, 'packingMode.0.unit1', '');
    },
    getTotalSum: function(forecastItem: ProjectedItem): number {
      return Number(forecastItem.quantities.q1) + Number(forecastItem.quantities.q2) + Number(forecastItem.quantities.q3) + Number(forecastItem.quantities.q4);
    },
    updateTotalSum: function(projectedItem: ProjectedItem) {
      const totalSum = Number(projectedItem.quantities.q1) + Number(projectedItem.quantities.q2) + Number(projectedItem.quantities.q3) + Number(projectedItem.quantities.q4);
      projectedItem.quantities.totalSum = totalSum;
    },
    forecastChangePercentage: function(invoicedItem: ProjectedItem, projectedItem: ProjectedItem, quarter: 'q1' | 'q2' | 'q3' | 'q4' | 'totalSum'): string {
      const invoicedQuantity: number = _.get(invoicedItem, `quantities.${quarter}`, 0);
      const projectedQuantity: number = _.get(projectedItem, `quantities.${quarter}`, 0);
      if(invoicedQuantity > 0) {
        const percentChange = ((projectedQuantity - invoicedQuantity) / invoicedQuantity) * 100;
        return `${Math.round(percentChange)}%`;
      }
      if(projectedQuantity > 0) return '100%';
      return '0%';
    },
    onDelete: function(itemId: number) {
      const projectedItems = this.projectedItems.filter((projectedItem: ProjectedItem) => projectedItem.itemId !== itemId);
      const { prefix, key } = this.parent._getPrefixAndKey(this.input.projectedItemsVariable);
      this.$set(this.getVariable(prefix), key, projectedItems );
    },
    addProduct: function() {
      if(!this.productId) return;
      this.projectedItems.push({
        itemId: this.productId,
        quantities: {
          q1: 0,
          q2: 0,
          q3: 0,
          q4: 0
        }
      });
    },
    showQuantityOrSAR: function(quantityOrSar: number | string): number | string {
      if(this.displayInSAR) return numFormat(Number(quantityOrSar), true);
      else return numberAbbreviation(quantityOrSar);
    },
    grandTotal: function(variableName: string): number {
      return this.groupedInvoicedItems.reduce(
        (total: number, groupItem: any) => total + Number(_.get(groupItem, variableName, 0)),
        0
      );
    },
    grandTotalPercentage: function(quarter: 'q1' | 'q2' | 'q3' | 'q4' | 'totalSum'): string {
      const invoicedQuantityTotal = this.grandTotal(`invoicedItem.quantities.${quarter}`);
      const projectedQuantityTotal = this.grandTotal(`projectedItem.quantities.${quarter}`);
      if(invoicedQuantityTotal > 0) {
        const percentChange = ((projectedQuantityTotal - invoicedQuantityTotal) / invoicedQuantityTotal) * 100;
        return `${Math.round(percentChange)}%`;
      }
      if(projectedQuantityTotal > 0) return '100%';
      return '0%';
    },
    getProductAlias: function(product: any): string {
      return productAlias(product);
    },
    getProductDisabledStatus: function(product: any): boolean {
      if(!product) return true;
      return product.status == 'Closed';
    }
  }
});
